import { useCallback, useEffect, useState } from 'react';

import { getThemeVersions } from '@src/services/storefront';
import type { ThemeVersions } from '@src/services/storefront';

export interface UseThemeVersionsResult {
  data: ThemeVersions | undefined;
  isLoading: boolean;
  error: Error | undefined;
  /** Re-fetch the current partner's versions (used by the manual refresh + after a deploy). */
  refetch: () => void;
}

/**
 * Fetches the storefront theme versions for a partner via the theme-versions client service.
 * Re-fetches when the partner changes or `refetch()` is called. No partner selected means no fetch.
 *
 * The read can fail (the production versions read may 500 — see the -be env-gate), so `error` is a
 * first-class state the page renders as an error, not a silent empty panel.
 */
export const useThemeVersions = (partnerCode: string | undefined): UseThemeVersionsResult => {
  const [data, setData] = useState<ThemeVersions | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const refetch = useCallback(() => setReloadToken(token => token + 1), []);

  useEffect(() => {
    if (!partnerCode) {
      setData(undefined);
      setError(undefined);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(undefined);

    getThemeVersions(partnerCode)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          const err = caught instanceof Error ? caught : new Error(String(caught));
          const status = (caught as { response?: { status?: number } })?.response?.status;
          console.error('useThemeVersions fetch failed:', { partnerCode, status, error: err.message });
          setData(undefined);
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [partnerCode, reloadToken]);

  return { data, isLoading, error, refetch };
};
